import { Calendar, Clock, Cloud, FileText, Ruler, Target, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import type { TestResult } from '../../types/testResults'
import { calculateValues, getTestType, getTestTypesByDiscipline } from '../../types/testTypes'
import { Button } from '../common/Button'
import { FormField } from '../common/FormField'
import { FormIcon } from '../common/FormIcon'
import { FormInput, FormSelect, FormTextarea } from '../common/FormInput'
import { FormLabel } from '../common/FormLabel'
import { Modal } from '../common/Modal'
import { formatCSS, formatSecondsToTime, parseTimeToSeconds } from './testResultUtils'

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg};
`

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const FormGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.sm} ${theme.spacing.xs};
  grid-template-columns: 40px 1fr 1fr;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const ResultInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  input:disabled {
    background: transparent;
  }
`

const UnitLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
  min-width: fit-content;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.lg};
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

interface TestResultModalProps {
  athleteId: string
  isOpen: boolean
  onClose: () => void
  testResult?: TestResult
  addTestResult: (testResult: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateTestResult: (id: string, testResult: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  deleteTestResult: (id: string) => Promise<void>
}

export const TestResultModal: React.FC<TestResultModalProps> = ({ 
  athleteId, 
  isOpen, 
  onClose, 
  testResult, 
  addTestResult, 
  updateTestResult, 
  deleteTestResult 
}) => {
  const [formData, setFormData] = useState({
    date: '',
    discipline: 'run' as TestResult['discipline'],
    testType: '',
    time: '',
    time400m: '',
    time200m: '',
    avgPower: '',
    maxPower: '',
    avgHeartRate: '',
    maxHeartRate: '',
    weather: '',
    notes: '',
    status: 'planned' as TestResult['status'],
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [calculatedValues, setCalculatedValues] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)

  // Initialize form data when modal opens or testResult changes
  useEffect(() => {
    if (isOpen) {
      if (testResult) {
        // Editing existing test result
        setFormData({
          date: testResult.date.split('T')[0], // Convert to YYYY-MM-DD format
          discipline: testResult.discipline || 'run',
          testType: testResult.testType || '',
          time: testResult.time ? formatSecondsToTime(testResult.time) : '',
          time400m: testResult.time400m ? formatSecondsToTime(testResult.time400m) : '',
          time200m: testResult.time200m ? formatSecondsToTime(testResult.time200m) : '',
          avgPower: testResult.avgPower?.toString() || '',
          maxPower: testResult.maxPower?.toString() || '',
          avgHeartRate: testResult.avgHeartRate?.toString() || '',
          maxHeartRate: testResult.maxHeartRate?.toString() || '',
          weather: testResult.weather || '',
          notes: testResult.notes || '',
          status: testResult.status,
        })
      } else {
        // Adding new test result
        setFormData({
          date: new Date().toISOString().split('T')[0], // Today's date
          discipline: 'run',
          testType: '5k', // Default to 5k for runs
          time: '',
          time400m: '',
          time200m: '',
          avgPower: '',
          maxPower: '',
          avgHeartRate: '',
          maxHeartRate: '',
          weather: '',
          notes: '',
          status: 'planned',
        })
      }
      setErrors({})
    }
  }, [isOpen, testResult])

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }

    // Recalculate values when relevant fields change
    if (['testType', 'time', 'time400m', 'time200m', 'avgPower'].includes(field)) {
      const testType = getTestType(field === 'testType' ? value : newFormData.testType)
      if (testType) {
        // Parse time fields from MM:SS format to seconds for calculations
        let timeSeconds: number | undefined
        let time400mSeconds: number | undefined
        let time200mSeconds: number | undefined

        if (field === 'time' || newFormData.time) {
          const timeValue = field === 'time' ? value : newFormData.time
          timeSeconds = parseTimeToSeconds(timeValue) || undefined
        }

        if (field === 'time400m' || newFormData.time400m) {
          const timeValue = field === 'time400m' ? value : newFormData.time400m
          time400mSeconds = parseTimeToSeconds(timeValue) || undefined
        }

        if (field === 'time200m' || newFormData.time200m) {
          const timeValue = field === 'time200m' ? value : newFormData.time200m
          time200mSeconds = parseTimeToSeconds(timeValue) || undefined
        }

        const calculated = calculateValues(testType, {
          time: timeSeconds,
          time400m: time400mSeconds,
          time200m: time200mSeconds,
          avgPower: parseFloat(newFormData.avgPower) || undefined,
        })
        setCalculatedValues(calculated)
      }
    }

    // Update test type when discipline changes
    if (field === 'discipline') {
      const availableTypes = getTestTypesByDiscipline(value as TestResult['discipline'])
      const newTestType = availableTypes.length > 0 ? availableTypes[0].id : ''
      setFormData((prev) => ({ ...prev, testType: newTestType }))
    }

    // Auto-set status to completed if date is in the past
    if (field === 'date') {
      const selectedDate = new Date(value)
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison

      if (selectedDate < today) {
        setFormData((prev) => ({ ...prev, status: 'completed' }))
      } else {
        setFormData((prev) => ({ ...prev, status: 'planned' }))
      }
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.discipline) newErrors.discipline = 'Discipline is required'
    if (!formData.testType) newErrors.testType = 'Test type is required'
    if (!formData.date) newErrors.date = 'Date is required'

    // For completed tests, validate based on test type requirements
    if (formData.status === 'completed') {
      const testType = getTestType(formData.testType)
      if (testType) {
        for (const field of testType.fields) {
          if (field.required) {
            const fieldValue = formData[field.key as keyof typeof formData]
            if (!fieldValue || parseFloat(fieldValue) <= 0) {
              newErrors[field.key] = `${field.label} is required for completed tests`
            }
          }
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return
    
    try {
      setError(null)
      
      const resultData: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'> = {
        userId: athleteId,
        description: `${getTestType(formData.testType)?.name || formData.testType} - ${new Date(formData.date).toLocaleDateString()}`,
        date: new Date(formData.date).toISOString(),
        discipline: formData.discipline,
        testType: formData.testType,
        time: formData.time ? parseTimeToSeconds(formData.time) || undefined : undefined,
        time400m: formData.time400m ? parseTimeToSeconds(formData.time400m) || undefined : undefined,
        time200m: formData.time200m ? parseTimeToSeconds(formData.time200m) || undefined : undefined,
        avgPower: formData.avgPower ? parseFloat(formData.avgPower) : undefined,
        maxPower: formData.maxPower ? parseFloat(formData.maxPower) : undefined,
        avgHeartRate: formData.avgHeartRate ? parseInt(formData.avgHeartRate) : undefined,
        maxHeartRate: formData.maxHeartRate ? parseInt(formData.maxHeartRate) : undefined,
        css: calculatedValues.css,
        ftp: calculatedValues.ftp,
        weather: formData.weather.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        status: formData.status,
      }

      if (testResult) {
        await updateTestResult(testResult?.id, resultData)
      } else {
        await addTestResult(resultData)
      }

      onClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save test result'
      setError(errorMessage)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setError(null)
      await deleteTestResult(id)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete test result'
      setError(errorMessage)
    }
  }

  if (!isOpen) return null

  const modalHeader = (
    <ModalHeader>
      <ModalTitle>{testResult ? 'Edit Test Result' : 'Add Test Result'}</ModalTitle>
      <CloseButton onClick={onClose}>
        <X size={18} />
      </CloseButton>
    </ModalHeader>
  )

  const modalFooter = (
    <ModalFooter>
      <div style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
        {testResult && (
          <Button
            variant="danger"
            onClick={async () => {
              await handleDelete(testResult.id)
              onClose()
            }}
          >
            Delete Test
          </Button>
        )}
      </div>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSave}>
        Save
      </Button>
    </ModalFooter>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="900px" header={modalHeader} footer={modalFooter}>
      <ModalBody>
        {error && <ErrorMessage style={{ marginBottom: '16px' }}>{error}</ErrorMessage>}
        <FormGrid>
          <FormField>
            <FormIcon>
              <Calendar size={16} />
            </FormIcon>
          </FormField>
          <FormField>
            <FormLabel>Date *</FormLabel>
            <FormInput
              type="date"
              variant="borderless"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
            />
            {errors.date && <ErrorMessage>{errors.date}</ErrorMessage>}
          </FormField>

          <FormField>
            <FormLabel>Status</FormLabel>
            <FormSelect
              variant="borderless"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="planned">Planned</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </FormSelect>
          </FormField>

          <FormField>
            <FormIcon>
              <Target size={16} />
            </FormIcon>
          </FormField>
          <FormField>
            <FormLabel>Discipline *</FormLabel>
            <FormSelect
              variant="borderless"
              value={formData.discipline}
              onChange={(e) => handleInputChange('discipline', e.target.value)}
            >
              <option value="running">Running</option>
              <option value="cycling">Cycling</option>
              <option value="swimming">Swim</option>
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel>Test Type *</FormLabel>
            <FormSelect
              variant="borderless"
              value={formData.testType}
              onChange={(e) => handleInputChange('testType', e.target.value)}
            >
              <option value="">Select test type</option>
              {getTestTypesByDiscipline(formData.discipline).map((testType) => (
                <option key={testType.id} value={testType.id}>
                  {testType.name}
                </option>
              ))}
            </FormSelect>
            {errors.testType && <ErrorMessage>{errors.testType}</ErrorMessage>}
          </FormField>

          <FormField></FormField>
          <FormField fullWidth={true}>
            <FormLabel>Results {formData.status === 'completed' ? '(at least one required)' : ''}</FormLabel>
            {errors.results && <ErrorMessage>{errors.results}</ErrorMessage>}
          </FormField>

          {/* Dynamic fields based on test type */}
          {formData.testType &&
            getTestType(formData.testType)?.fields.map((field, index) => {
              const testTypeDefinition = getTestType(formData.testType)
              const isFirstField = index === 0

              return (
                <React.Fragment key={field.key}>
                  {isFirstField && (
                    <FormField>
                      <FormIcon>
                        <Clock size={16} />
                      </FormIcon>
                    </FormField>
                  )}
                  {!isFirstField && <FormField></FormField>}

                  <FormField>
                    <FormLabel>
                      {field.label} {field.required ? '*' : ''}
                    </FormLabel>
                    <ResultInputContainer>
                      <FormInput
                        type={field.type === 'time' ? 'text' : 'number'}
                        variant="borderless"
                        step={field.type === 'heartRate' ? '1' : '0.01'}
                        placeholder={field.placeholder || '0'}
                        value={formData[field.key as keyof typeof formData] || ''}
                        onChange={(e) => handleInputChange(field.key as keyof typeof formData, e.target.value)}
                        disabled={formData.status !== 'completed'}
                      />
                      <UnitLabel>{field.unit}</UnitLabel>
                    </ResultInputContainer>
                    {errors[field.key] && <ErrorMessage>{errors[field.key]}</ErrorMessage>}
                  </FormField>

                  <FormField></FormField>
                </React.Fragment>
              )
            })}

          {/* Show calculated values */}
          {Object.keys(calculatedValues).length > 0 && (
            <>
              <FormField></FormField>
              <FormField fullWidth={true}>
                <FormLabel>Calculated Values</FormLabel>
                {Object.entries(calculatedValues).map(([key, value]) => {
                  const testTypeDefinition = getTestType(formData.testType)
                  const calculatedField = testTypeDefinition?.calculatedFields?.find((f) => f.key === key)
                  if (!calculatedField) return null

                  return (
                    <ResultInputContainer key={key} style={{ marginBottom: '8px' }}>
                      <FormInput
                        type="text"
                        variant="borderless"
                        value={key === 'css' ? formatCSS(value) : value.toFixed(0)}
                        readOnly
                        disabled
                      />
                      <UnitLabel>
                        {key === 'css' ? 'CSS' : `${calculatedField.unit} ${calculatedField.label}`}
                      </UnitLabel>
                    </ResultInputContainer>
                  )
                })}
              </FormField>
            </>
          )}

          <FormField>
            <FormIcon>
              <Cloud size={16} />
            </FormIcon>
          </FormField>
          <FormField fullWidth={true}>
            <FormLabel>Weather</FormLabel>
            <FormTextarea
              variant="borderless"
              placeholder="Weather conditions during the test..."
              value={formData.weather}
              onChange={(e) => handleInputChange('weather', e.target.value)}
            />
          </FormField>

          <FormField>
            <FormIcon>
              <FileText size={16} />
            </FormIcon>
          </FormField>
          <FormField fullWidth={true}>
            <FormLabel>Notes</FormLabel>
            <FormTextarea
              variant="borderless"
              placeholder="Additional notes about the test..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />
          </FormField>
        </FormGrid>
      </ModalBody>
    </Modal>
  )
}
